import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Banknote, AlertTriangle, Store, HandCoins, Wallet, TrendingUp, TrendingDown,
  ArrowRightLeft, ChevronRight, RefreshCw,
} from "lucide-react";
import { CurrencyDisplay } from "./CurrencySettings";

const CashHandoverForm = ({ user, onSuccess }) => {
  const [formData, setFormData] = useState({
    handover_date: new Date().toISOString().split('T')[0],
    handed_over_to: "",
    remarks: "",
  });

  const [restaurantBalances, setRestaurantBalances] = useState([]);
  const [restaurantAmounts, setRestaurantAmounts] = useState({});
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  useEffect(() => {
    fetchRestaurantBalances();
  }, [formData.handover_date]);

  const fetchRestaurantBalances = async () => {
    setBalanceLoading(true);
    try {
      const res = await axiosInstance.get("/restaurant-cash/balances");
      setRestaurantBalances(res.data);
    } catch (error) {
      console.error("Error fetching balances:", error);
      setRestaurantBalances([]);
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleAmountChange = (restaurantId, value) => {
    setRestaurantAmounts(prev => ({ ...prev, [restaurantId]: value }));
  };

  const totalAvailable = restaurantBalances.reduce((sum, b) => sum + (b.current_balance || 0), 0);
  const totalHandover = Object.values(restaurantAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  const hasValidAmounts = totalHandover > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasValidAmounts) { toast.error("Enter at least one restaurant amount"); return; }
    if (!formData.handed_over_to.trim()) { toast.error("Please enter who the cash was handed over to"); return; }

    for (const b of restaurantBalances) {
      const amt = parseFloat(restaurantAmounts[b.restaurant_id]) || 0;
      if (amt > 0 && amt > b.current_balance) {
        toast.error(`${b.restaurant_name}: Cannot handover ₹${amt} — only ₹${b.current_balance.toFixed(0)} available`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const restaurant_amounts = restaurantBalances
        .filter(b => parseFloat(restaurantAmounts[b.restaurant_id]) > 0)
        .map(b => ({
          restaurant_id: b.restaurant_id,
          restaurant_name: b.restaurant_name,
          amount: parseFloat(restaurantAmounts[b.restaurant_id]),
        }));

      await axiosInstance.post("/cash-handover", {
        handover_date: formData.handover_date,
        cash_handed_over: totalHandover,
        handed_over_to: formData.handed_over_to,
        remarks: formData.remarks,
        restaurant_amounts,
      });

      toast.success("Cash handover recorded successfully!");
      setFormData({ handover_date: new Date().toISOString().split('T')[0], handed_over_to: "", remarks: "" });
      setRestaurantAmounts({});
      fetchRestaurantBalances();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record cash handover");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" data-testid="cash-handover-title">Cash Handover</h2>
          <p className="text-muted-foreground">Collect cash from restaurants and record handover</p>
        </div>
      </div>

      {/* KPI Card — Total Available Cash */}
      <Card
        className="shadow-lg border-l-4 border-l-emerald-500 bg-gradient-to-r from-emerald-50 to-teal-50 cursor-pointer hover:shadow-xl transition-shadow"
        onClick={() => setBreakdownOpen(true)}
        data-testid="total-cash-kpi"
      >
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Total Available Cash</p>
                <p className="text-3xl font-bold text-emerald-700" data-testid="total-available-cash">
                  <CurrencyDisplay amount={totalAvailable} />
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Across {restaurantBalances.length} restaurants</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Breakdown Dialog */}
      <Dialog open={breakdownOpen} onOpenChange={setBreakdownOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-600" /> Cash Breakdown by Restaurant
            </DialogTitle>
            <DialogDescription>Current cash balance at each restaurant</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {restaurantBalances.map(b => (
              <div key={b.restaurant_id} className="p-3 rounded-lg border bg-gray-50/50" data-testid={`breakdown-${b.restaurant_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm">{b.restaurant_name}</span>
                  <span className={`text-lg font-bold ${b.current_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    <CurrencyDisplay amount={b.current_balance} />
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Banknote className="w-3 h-3 text-blue-500" /> Opening: <CurrencyDisplay amount={b.opening_balance} /></span>
                  <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-green-500" /> Revenue: <CurrencyDisplay amount={b.cash_revenue} /></span>
                  <span className="flex items-center gap-1"><TrendingDown className="w-3 h-3 text-red-500" /> Expenses: <CurrencyDisplay amount={b.cash_expenses} /></span>
                  <span className="flex items-center gap-1"><ArrowRightLeft className="w-3 h-3 text-purple-500" /> Transfers: <CurrencyDisplay amount={b.incoming_transfers - b.outgoing_transfers} /></span>
                  {(b.cash_handovers || 0) > 0 && (
                    <span className="flex items-center gap-1 col-span-2"><HandCoins className="w-3 h-3 text-amber-500" /> Handovers: <CurrencyDisplay amount={b.cash_handovers} /></span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="pt-3 border-t flex items-center justify-between">
            <span className="text-sm text-gray-500">System Total</span>
            <span className="text-xl font-bold text-emerald-700"><CurrencyDisplay amount={totalAvailable} /></span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBreakdownOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Handover Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="shadow-sm">
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Handover Date *</Label>
              <Input type="date" value={formData.handover_date} onChange={(e) => setFormData({ ...formData, handover_date: e.target.value })} max={new Date().toISOString().split('T')[0]} required data-testid="handover-date" />
            </div>
            <div className="space-y-2">
              <Label>Handed Over To *</Label>
              <Input type="text" placeholder="Name of person receiving cash" value={formData.handed_over_to} onChange={(e) => setFormData({ ...formData, handed_over_to: e.target.value })} required data-testid="handed-to" />
            </div>
            <div className="space-y-2">
              <Label>Remarks (Optional)</Label>
              <Input placeholder="Add notes..." value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} data-testid="handover-remarks" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Store className="w-4 h-4" /> Cash Collection per Restaurant
            </CardTitle>
            <CardDescription>Enter the amount being collected from each restaurant</CardDescription>
          </CardHeader>
          <CardContent>
            {balanceLoading ? (
              <div className="text-gray-500 text-center py-6">Loading...</div>
            ) : restaurantBalances.length === 0 ? (
              <div className="text-gray-500 text-center py-6">No restaurants found</div>
            ) : (
              <div className="space-y-3">
                {restaurantBalances.map(b => {
                  const amt = parseFloat(restaurantAmounts[b.restaurant_id]) || 0;
                  const exceeds = amt > b.current_balance;
                  return (
                    <div key={b.restaurant_id} className="flex items-center gap-4 p-3 rounded-lg border bg-gray-50/50 hover:bg-gray-50" data-testid={`handover-row-${b.restaurant_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{b.restaurant_name}</p>
                        <p className="text-xs text-gray-500">Available: <span className={`font-semibold ${b.current_balance > 0 ? 'text-green-600' : 'text-gray-400'}`}><CurrencyDisplay amount={b.current_balance} /></span></p>
                      </div>
                      <div className="w-40">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">₹</span>
                          <Input type="number" step="0.01" min="0" placeholder="0" value={restaurantAmounts[b.restaurant_id] || ""} onChange={(e) => handleAmountChange(b.restaurant_id, e.target.value)} className={`pl-8 h-9 text-sm ${exceeds ? 'border-red-400 focus:ring-red-400' : ''}`} data-testid={`handover-amount-${b.restaurant_id}`} />
                        </div>
                        {exceeds && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Exceeds balance</p>}
                        {amt > 0 && !exceeds && <p className="text-xs text-gray-500 mt-1">Left: <span className="font-semibold text-green-600"><CurrencyDisplay amount={b.current_balance - amt} /></span></p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={`shadow-lg border-l-4 ${hasValidAmounts ? 'border-l-green-500' : 'border-l-gray-300'}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                  <HandCoins className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Handover</p>
                  <p className="text-2xl font-bold" data-testid="total-handover-amount"><CurrencyDisplay amount={totalHandover} /></p>
                </div>
              </div>
              <Button type="submit" disabled={submitting || !hasValidAmounts} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8" data-testid="submit-handover">
                {submitting ? "Recording..." : "Record Handover"}
              </Button>
            </div>
            {hasValidAmounts && (
              <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                {restaurantBalances.filter(b => parseFloat(restaurantAmounts[b.restaurant_id]) > 0).map(b => (
                  <span key={b.restaurant_id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-xs border border-amber-200">
                    {b.restaurant_name}: <CurrencyDisplay amount={parseFloat(restaurantAmounts[b.restaurant_id])} />
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default CashHandoverForm;
